import {get, isEmpty} from 'lodash'
import {sortByKey} from 'helpers/sorting'
import {isPrintTemplateLegacy} from 'helpers/printTemplates/index'

export const PrintTemplateType = {
  DRAFT: 'DRAFT',
  QUOTATION: 'QUOTATION',
  CONFIRMATION: 'CONFIRMATION',
  ACCEPTANCE: 'ACCEPTANCE',
  PROFORMA_INVOICE: 'PROFORMA_INVOICE',
  INVOICE: 'INVOICE',
  CREDIT_NOTE: 'CREDIT_NOTE',
  DELIVERY_NOTE: 'DELIVERY_NOTE',
  RETURN_NOTE: 'RETURN_NOTE',
  DRAFT_PURCHASE_ORDER: 'DRAFT_PURCHASE_ORDER',
  PURCHASE_ORDER: 'PURCHASE_ORDER',
  PURCHASE_ORDER_ACCEPTANCE: 'PURCHASE_ORDER_ACCEPTANCE',
}

export const getPrintTemplateTypeLabel = (printTemplateType) => {
  switch (printTemplateType) {
    case PrintTemplateType.DRAFT:
      return 'Draft Quotation'
    case PrintTemplateType.QUOTATION:
      return 'Quotation'
    case PrintTemplateType.CONFIRMATION:
      return 'Quote Confirmation'
    case PrintTemplateType.ACCEPTANCE:
      return 'Quote Acceptance'
    case PrintTemplateType.PROFORMA_INVOICE:
      return 'Proforma Invoice'
    case PrintTemplateType.INVOICE:
      return 'Invoice'
    case PrintTemplateType.CREDIT_NOTE:
      return 'Credit Note'
    case PrintTemplateType.DELIVERY_NOTE:
      return 'Delivery Note'
    case PrintTemplateType.RETURN_NOTE:
      return 'Return Note'
    case PrintTemplateType.DRAFT_PURCHASE_ORDER:
      return 'Draft Purchase Order'
    case PrintTemplateType.PURCHASE_ORDER:
      return 'Purchase Order'
    case PrintTemplateType.PURCHASE_ORDER_ACCEPTANCE:
      return 'Purchase Order Acceptance'
    default:
      return null
  }
}

export const PrintTemplateStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
}

export const PrintTemplateRouteType = {
  QUOTING: 'quoting',
  PURCHASING: 'purchasing',
  DELIVERIES: 'deliveries',
  INVOICING: 'invoicing',
}

export const getPrintTemplateGroupRootLabel = (printTemplateRouteType) => {
  switch (printTemplateRouteType) {
    case PrintTemplateRouteType.QUOTING:
      return 'Quoting Templates'
    case PrintTemplateRouteType.PURCHASING:
      return 'Purchasing Templates'
    case PrintTemplateRouteType.DELIVERIES:
      return 'Deliveries Templates'
    case PrintTemplateRouteType.INVOICING:
      return 'Invoicing Templates'
    default:
      return null
  }
}

export const getPrintTemplateTypeOptions = (printTemplateRouteType) => {
  const printTemplateTypes = getPrintTemplateTypes(printTemplateRouteType)
  return printTemplateTypes.map(printTemplateType => {
    return {
      key: printTemplateType,
      value: printTemplateType,
      label: getPrintTemplateTypeLabel(printTemplateType),
    }
  })
}

export const getPrintTemplateStatusOptions = () => {
  return Object.values(PrintTemplateStatus).map(printTemplateStatus => {
    return {
      key: printTemplateStatus,
      value: printTemplateStatus,
      label: getPrintTemplateStatusLabel(printTemplateStatus),
    }
  })
}


// Page size presets offered in the editor - a curated set of common document
// sizes (values must match react-pdf's preset names). Ordered with the most
// commonly used first.
const PAGE_SIZE_PRESETS = [
  {value: 'A4', label: 'A4 (210 × 297 mm)'},
  {value: 'LETTER', label: 'Letter (216 × 279 mm)'},
  {value: 'A5', label: 'A5 (148 × 210 mm)'},
  {value: 'LEGAL', label: 'Legal (216 × 356 mm)'},
  {value: 'A3', label: 'A3 (297 × 420 mm)'},
  {value: 'TABLOID', label: 'Tabloid (279 × 432 mm)'},
  {value: 'EXECUTIVE', label: 'Executive (184 × 267 mm)'},
  {value: 'FOLIO', label: 'Folio (216 × 330 mm)'},
  {value: 'A6', label: 'A6 (105 × 148 mm)'},
  // ISO A series (within range)
  {value: 'A2', label: 'A2 (420 × 594 mm)'},
  {value: 'A1', label: 'A1 (594 × 841 mm)'},
  {value: 'A0', label: 'A0 (841 × 1189 mm)'},
  // ISO B series (within range)
  {value: 'B0', label:
      'B0 (1000 × 1414 mm)'},
  {value: 'B1', label: 'B1 (707 × 1000 mm)'},
  {value: 'B2', label: 'B2 (500 × 707 mm)'},
  {value: 'B3', label: 'B3 (353 × 500 mm)'},
  {value: 'B4', label: 'B4 (250 × 353 mm)'},
  {value: 'B5', label: 'B5 (176 × 250 mm)'},
  {value: 'B6', label: 'B6 (125 × 176 mm)'},
  // ISO C series / envelopes (within range)
  {value: 'C0', label: 'C0 (917 × 1297 mm)'},
  {value: 'C1', label: 'C1 (648 × 917 mm)'},
  {value: 'C2', label: 'C2 (458 × 648 mm)'},
  {value: 'C3', label: 'C3 (324 × 458 mm)'},
  {value: 'C4', label: 'C4 (229 × 324 mm)'},
  {value: 'C5', label: 'C5 (162 × 229 mm)'},
  {value: 'C6', label: 'C6 (114 × 162 mm)'},
  // RA / SRA series (within range)
  {value: 'RA0', label: 'RA0 (860 × 1220 mm)'},
  {value: 'RA1', label: 'RA1 (610 × 860 mm)'},
  {value: 'RA2', label: 'RA2 (430 × 610 mm)'},
  {value: 'RA3', label: 'RA3 (305 × 430 mm)'},
  {value: 'RA4', label: 'RA4 (215 × 305 mm)'},
  {value: 'SRA0', label: 'SRA0 (900 × 1280 mm)'},
  {value: 'SRA1', label: 'SRA1 (640 × 900 mm)'},
  {value: 'SRA2', label: 'SRA2 (450 × 640 mm)'},
  {value: 'SRA3', label: 'SRA3 (320 × 450 mm)'},
  {value: 'SRA4', label: 'SRA4 (225 × 320 mm)'},
]

export const PageSize = PAGE_SIZE_PRESETS.reduce(
    (sizes, {value}) => ({...sizes, [value]: value}),
    {CUSTOM: 'CUSTOM'}
)

export const DEFAULT_PAGE_SIZE = PageSize.A4

export const getPageSizeLabel = (pageSize) => {
  if (pageSize === PageSize.CUSTOM)
    return 'Custom'

  const preset = PAGE_SIZE_PRESETS.find(({value}) => value === pageSize)
  return preset ? preset.label : pageSize
}


export const getPageSizeOptions = () => {
  const presetOptions = PAGE_SIZE_PRESETS.map(({value, label}) => ({
    key: value,
    value,
    label,
  }))

  return [
    {
      key: PageSize.CUSTOM,
      value: PageSize.CUSTOM,
      label: 'Custom',
    },
    ...presetOptions,
  ]
}

export const Orientation = {
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape',
}

export const DEFAULT_ORIENTATION = Orientation.PORTRAIT

export const getOrientationLabel = (orientation) => {
  switch (orientation) {
    case Orientation.PORTRAIT:
      return 'Portrait'
    case Orientation.LANDSCAPE:
      return 'Landscape'
    default:
      return null
  }
}

export const getOrientationOptions = () => {
  return Object.values(Orientation).map(orientation => {
    return {
      key: orientation,
      value: orientation,
      label: getOrientationLabel(orientation),
    }
  })
}

export const getDefaultPrintTemplateType = (printTemplateRouteType) => {
  switch (printTemplateRouteType) {
    case PrintTemplateRouteType.QUOTING:
      return PrintTemplateType.DRAFT
    case PrintTemplateRouteType.PURCHASING:
      return PrintTemplateType.DRAFT_PURCHASE_ORDER
    case PrintTemplateRouteType.DELIVERIES:
      return PrintTemplateType.DELIVERY_NOTE
    case PrintTemplateRouteType.INVOICING:
      return PrintTemplateType.INVOICE
    default:
      return null
  }
}

export const getDefaultPrintTemplateGroup = (printTemplateRouteType, printTemplateGroups) => {
  const printTemplateType = getDefaultPrintTemplateType(printTemplateRouteType)
  const filteredPrintTemplateGroups = printTemplateGroups
      .filter(printTemplateGroup => get(printTemplateGroup, 'printTemplateType', null) === printTemplateType)

  return isEmpty(filteredPrintTemplateGroups)
      ? null
      : filteredPrintTemplateGroups[0]
}

export const getPrintTemplateTypes = (printTemplateRouteType) => {
  switch (printTemplateRouteType) {
    case PrintTemplateRouteType.QUOTING:
      return [
        PrintTemplateType.DRAFT,
        PrintTemplateType.QUOTATION,
        PrintTemplateType.CONFIRMATION,
        PrintTemplateType.ACCEPTANCE,
        PrintTemplateType.PROFORMA_INVOICE,
      ]
    case PrintTemplateRouteType.PURCHASING:
      return [
        PrintTemplateType.DRAFT_PURCHASE_ORDER,
        PrintTemplateType.PURCHASE_ORDER,
        PrintTemplateType.PURCHASE_ORDER_ACCEPTANCE,
      ]
    case PrintTemplateRouteType.DELIVERIES:
      return [
        PrintTemplateType.DELIVERY_NOTE,
        PrintTemplateType.RETURN_NOTE,
      ]
    case PrintTemplateRouteType.INVOICING:
      return [
        PrintTemplateType.INVOICE,
        PrintTemplateType.CREDIT_NOTE,
      ]
    default:
      return []
  }
}

export const getPrintTemplateRouteTypeFromTemplateType = (templateType) => {
  switch (templateType) {
    case PrintTemplateType.DRAFT:
      return PrintTemplateRouteType.QUOTING

    case PrintTemplateType.QUOTATION:
      return PrintTemplateRouteType.QUOTING

    case PrintTemplateType.CONFIRMATION:
      return PrintTemplateRouteType.QUOTING

    case PrintTemplateType.ACCEPTANCE:
      return PrintTemplateRouteType.QUOTING

    case PrintTemplateType.PROFORMA_INVOICE:
      return PrintTemplateRouteType.QUOTING

    case PrintTemplateType.DRAFT_PURCHASE_ORDER:
      return PrintTemplateRouteType.PURCHASING

    case PrintTemplateType.PURCHASE_ORDER:
      return PrintTemplateRouteType.PURCHASING

    case PrintTemplateType.PURCHASE_ORDER_ACCEPTANCE:
      return PrintTemplateRouteType.PURCHASING

    case PrintTemplateType.DELIVERY_NOTE:
      return PrintTemplateRouteType.DELIVERIES

    case PrintTemplateType.RETURN_NOTE:
      return PrintTemplateRouteType.DELIVERIES

    case PrintTemplateType.INVOICE:
      return PrintTemplateRouteType.INVOICING

    case PrintTemplateType.CREDIT_NOTE:
      return PrintTemplateRouteType.INVOICING

    default:
      return null
  }
}

export const getPrintTemplateGroupsForTypes = (printTemplateGroups, printTemplateTypes) => {
  return printTemplateGroups.filter(printTemplateGroup => {
    return printTemplateTypes.includes(get(printTemplateGroup, 'printTemplateType', null))
  })
}

export const getPrintTemplateGroup = (printTemplateGroups, printTemplateGroupId) => {
  return printTemplateGroups.find(printTemplateGroup => printTemplateGroup.id === printTemplateGroupId)
}

export const getPrintTemplatesForGroupId = (printTemplates, printTemplateGroupId) => {
  return printTemplates.filter(printTemplate => {
    return get(printTemplate, 'printTemplateGroupId', null) === printTemplateGroupId
  })
}

export const getPrintTemplatesForTypes = (printTemplates, printTemplateTypes) => {
  return printTemplates.filter(printTemplate => {
    return printTemplateTypes.includes(get(printTemplate, 'type', null))
  })
}

export const isPrintTemplatePublished = (status) => {
  return status === PrintTemplateStatus.PUBLISHED
}

export const getPrintTemplateStatusLabel = (status) => {
  switch (status) {
    case PrintTemplateStatus.DRAFT:
      return 'Draft'
    case PrintTemplateStatus.PUBLISHED:
      return 'Published'
    default:
      return null
  }
}

export const getPrintTemplateOptions = (printTemplates, printTemplateTypes) => {
  const printTemplatesForTypes = getPrintTemplatesForTypes(printTemplates, printTemplateTypes)
  return printTemplatesForTypes
      .filter(printTemplate => printTemplate.accountId !== 0)
      .filter(printTemplate => !isPrintTemplateLegacy(printTemplate))
      .sort((a, b) => sortByKey(a, b, 'name'))
      .map(printTemplate => {
        const printTemplateId = get(printTemplate, 'id', null)
        return {
          key: printTemplateId,
          value: printTemplateId,
          label: get(printTemplate, 'name', null),
          printTemplate,
        }
      })
}

export const getQuotingPrintTemplateSettingSegments = () => {
  const tabTypes = [
    PrintTemplateType.DRAFT,
    PrintTemplateType.QUOTATION,
    PrintTemplateType.CONFIRMATION,
    PrintTemplateType.PROFORMA_INVOICE,
  ]
  return tabTypes.map(printTemplateType => {
    return {
      id: printTemplateType,
      title: getPrintTemplateTypeLabel(printTemplateType),
    }
  })
}

export const getQuotingPrintTemplateSettingData = (selectedType, settings) => {
  switch (selectedType) {
    case PrintTemplateType.DRAFT:
      return {
        bodyKey: 'DRAFT_QUOTE_EMAIL_BODY',
        bodyDesc: 'Draft Quotation Email Body',
        bodyValue: get(settings, 'draftQuoteEmailBody'),
        subjectKey: 'DRAFT_QUOTE_EMAIL_SUBJECT',
        subjectDesc: 'Draft Quotation Email Subject',
        subjectValue: get(settings, 'draftQuoteEmailSubject'),
      }
    case PrintTemplateType.QUOTATION:
      return {
        bodyKey: 'QUOTE_EMAIL_BODY',
        bodyDesc: 'Quotation Email Body',
        bodyValue: get(settings, 'quoteEmailBody'),
        subjectKey: 'QUOTE_EMAIL_SUBJECT',
        subjectDesc: 'Quotation Email Subject',
        subjectValue: get(settings, 'quoteEmailSubject'),
      }
    case PrintTemplateType.CONFIRMATION:
      return {
        bodyKey: 'CONFIRMED_QUOTE_EMAIL_BODY',
        bodyDesc: 'Confirmation Email Body',
        bodyValue: get(settings, 'confirmedQuoteEmailBody'),
        subjectKey: 'CONFIRMED_QUOTE_EMAIL_SUBJECT',
        subjectDesc: 'Confirmation Email Subject',
        subjectValue: get(settings, 'confirmedQuoteEmailSubject'),
      }
    case PrintTemplateType.PROFORMA_INVOICE:
      return {
        bodyKey: 'PROFORMA_INVOICE_EMAIL_BODY',
        bodyDesc: 'Proforma Invoice Email Body',
        bodyValue: get(settings, 'proformaInvoiceEmailBody'),
        subjectKey: 'PROFORMA_INVOICE_EMAIL_SUBJECT',
        subjectDesc: 'Proforma Invoice Email Subject',
        subjectValue: get(settings, 'proformaInvoiceEmailSubject'),
      }
    default:
      return {}
  }
}

export const isPurchaseOrderType = (printTemplate) => {
  const type = get(printTemplate, 'type')
  return type === PrintTemplateType.PURCHASE_ORDER
}
